import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Target, Eye, Heart, Users, Tractor } from 'lucide-react';
import { Footer } from './LandingPage'; // Reutilizando o footer

const PageHeader = ({ title, subtitle }) => (
  <div className="relative bg-green-900/20 py-20 sm:py-32">
    <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
    <div className="container mx-auto px-4 text-center relative z-10">
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="text-4xl md:text-6xl font-bold tracking-tight text-white"
      >
        {title}
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
        className="mt-4 max-w-3xl mx-auto text-lg md:text-xl text-green-100/80"
      >
        {subtitle}
      </motion.p>
    </div>
  </div>
);

const AboutSection = () => {
  const values = [
    {
      icon: <Tractor className="w-10 h-10 text-amber-400" />,
      title: "Foco no Produtor",
      description: "Nossa paixão é o campo. Desenvolvemos cada funcionalidade pensando na realidade e nas necessidades de quem produz."
    },
    {
      icon: <Heart className="w-10 h-10 text-amber-400" />,
      title: "Simplicidade e Poder",
      description: "Acreditamos que a tecnologia mais avançada é aquela que qualquer pessoa pode usar. Unimos poder de análise com uma interface intuitiva."
    },
    {
      icon: <Users className="w-10 h-10 text-amber-400" />,
      title: "Confiança e Transparência",
      description: "Seus dados são seu maior ativo. Tratamos suas informações com a máxima segurança e clareza, como uma verdadeira parceria."
    }
  ];

  return (
    <div className="py-20 sm:py-24 bg-gray-950 text-white">
      <div className="container mx-auto px-4">
        {/* Nossa História */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto text-center mb-20"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Nossa História: Da Planilha ao Futuro do Agro</h2>
          <p className="text-lg text-green-100/80 leading-relaxed">
            O AgroGestão nasceu da necessidade real de um produtor rural cansado de planilhas complexas e dados desencontrados. Vimos de perto a dificuldade em calcular o custo real de uma safra, em consolidar informações de várias fazendas e em apresentar relatórios confiáveis para bancos. Decidimos que era hora de mudar esse cenário. Unimos especialistas em agronegócio e tecnologia para criar uma plataforma que não apenas resolve esses problemas, mas que impulsiona o produtor para uma nova era de gestão: mais inteligente, mais lucrativa e muito mais simples.
          </p>
        </motion.div>

        {/* Missão e Visão */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="bg-green-900/30 p-8 rounded-lg border border-green-800/50"
          >
            <div className="flex items-center gap-4 mb-4">
              <Target className="w-12 h-12 text-amber-400" />
              <h3 className="text-2xl font-bold">Nossa Missão</h3>
            </div>
            <p className="text-green-100/90 text-lg">
              Empoderar cada produtor rural com tecnologia de ponta, transformando dados complexos em decisões claras e lucrativas, garantindo a sustentabilidade e o crescimento do seu negócio.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="bg-green-900/30 p-8 rounded-lg border border-green-800/50"
          >
            <div className="flex items-center gap-4 mb-4">
              <Eye className="w-12 h-12 text-amber-400" />
              <h3 className="text-2xl font-bold">Nossa Visão</h3>
            </div>
            <p className="text-green-100/90 text-lg">
              Ser a plataforma de gestão rural líder e mais amada do Brasil, reconhecida por sua simplicidade, poder de análise e pelo impacto positivo real na vida e nos resultados dos produtores.
            </p>
          </motion.div>
        </div>

        {/* Nossos Valores */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white">Nossos Valores</h2>
          <p className="mt-4 text-lg text-green-200/70 max-w-2xl mx-auto">
            Os pilares que guiam cada decisão que tomamos.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-gray-900 p-8 rounded-lg border border-green-800/40 text-center"
            >
              <div className="inline-block p-4 bg-green-900/50 rounded-full mb-6">
                {value.icon}
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
              <p className="text-green-100/80">{value.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CtaSobre = () => (
    <section className="py-20 sm:py-24 bg-green-900/10">
        <div className="container mx-auto px-4 text-center">
            <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
            >
                <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">Pronto para transformar a gestão da sua fazenda?</h2>
                <p className="mt-4 text-lg text-green-200/70 max-w-xl mx-auto">
                    Junte-se a produtores que já estão colhendo os frutos de uma gestão mais inteligente.
                </p>
                <div className="mt-8">
                    <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-green-950 font-bold text-base w-full sm:w-auto group">
                        <Link to="/">
                            Experimentar grátis agora
                            <ArrowRight className="w-5 h-5 ml-2 transform transition-transform group-hover:translate-x-1" />
                        </Link>
                    </Button>
                </div>
            </motion.div>
        </div>
    </section>
);


export default function Sobre() {
  return (
    <div className="bg-gray-950">
      <PageHeader 
        title="Construindo o Futuro do Agronegócio"
        subtitle="Nossa missão é dar ao produtor rural o poder da tecnologia para simplificar a gestão e maximizar resultados."
      />
      <AboutSection />
      <CtaSobre />
      <Footer />
    </div>
  );
}
